import { ConfigProvider, Spin } from 'antd'
import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpApi from 'i18next-http-backend'
import { set } from 'lodash'
import moment from 'moment'
import React from 'react'
import { initReactI18next, useTranslation } from 'react-i18next'

const momentConfig: any = {
  week: {
    dow: 1,
  },
  longDateFormat: {
    l: 'YYYY.MM.DD',
    ll: 'YYYY-MM-DD',
  },
}

const initI18n = (language: string | undefined, getLocale: (s: string) => Promise<any>) => {
  return i18next
    .use(HttpApi)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: language,
      detection: {
        order: ['localStorage', 'navigator'],
      },
      returnEmptyString: false,
      fallbackLng: 'zh',
      debug: false,
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
      backend: {
        loadPath: '{{lng}}',
        request: async (_options, url, _payload, callback) => {
          try {
            const fulfilledLocale = await getLocale(url)
            const locale = {}
            for (const prop in fulfilledLocale) {
              set(locale, prop, fulfilledLocale[prop])
            }
            if ('default' in fulfilledLocale) {
              for (const p in fulfilledLocale['default']) {
                set(locale, p, fulfilledLocale['default'][p])
              }
            }
            callback(null, { status: 200, data: locale as string })
          } catch (e) {
            callback(null, { status: 404, data: '' })
          }
        },
      },
    })
}

interface I18nProps {
  language?: string
  getLocale: (s: string) => Promise<any>
}

export const InitI18n: React.FC<I18nProps> = ({ language, getLocale, children }) => {
  const [loading, setLoading] = React.useState(true)
  React.useEffect(() => {
    initI18n(language, getLocale).then(() => setLoading(false))
  }, [])
  return loading ? null : (children as any)
}

export const I18n: React.FC = ({ children }) => {
  const { i18n } = useTranslation()
  const [locale, setLocale] = React.useState<any>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (i18n.resolvedLanguage.includes('zh')) {
      // @ts-ignore
      import('moment/dist/locale/zh-cn').then(() => {
        moment.locale('zh-cn')
        moment.updateLocale('zh-cn', momentConfig)
      })
      import('antd/lib/locale-provider/zh_CN').then((res) => {
        setLocale(res.default)
      })
    } else {
      moment.locale('en')
      moment.updateLocale('en', momentConfig)
      setLocale(null)
    }
  }, [i18n.language])

  React.useEffect(() => {
    i18n.on('languageChanging', () => {
      setLoading(true)
    })
    i18n.on('languageChanged', () => {
      setLoading(false)
    })
  }, [])

  return (
    <Spin size='large' spinning={loading} delay={300}>
      <ConfigProvider locale={locale}>{children}</ConfigProvider>
    </Spin>
  )
}
